import { Button } from 'primereact/button'
import { DataTableFilterMeta, DataTablePageEvent } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { InputTextarea } from 'primereact/inputtextarea'
import { Panel } from 'primereact/panel'
import { classNames } from 'primereact/utils'
import { FC, useEffect, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'
import { useGetProject, useUpdateProject, useUpdateProjectDescription } from '../api/project-API'
import { useCreateRun, useGetSubmissions } from '../api/submission-API'
import { useGetVehicle } from '../api/vehicle-API'
import Breadcrumb, { BreadcrumbItem } from '../components/Breadcrumb'
import { CollapseIcon, EditIcon, ExpandIcon, PlusIcon } from '../components/Icons'
import IconButton from '../components/buttons/IconButton'
import ProjectForm from '../components/project/ProjectForm'
import SubmissionList from '../components/submission/SubmissionList'
import SubmissionModal from '../components/submission/SubmissionModal'
import { protoService } from '../proto/ProtoService'
import simUploadQueueStore from '../services/simUploadQueueStore'
import {
  Geometry,
  LazyTableState,
  Project,
  ProjectConfig,
  ProjectStatus,
  RunConfigProto,
  RunStatus,
  User,
  WorkflowSubmission,
  WorkflowSubmissionTemplate
} from '../types/types'
import {
  cloneAttitudeSet,
  cloneDomainSet,
  cloneMethodologySet,
  cloneSimulationSet,
  cloneTyreSet,
  cloneUser,
  queryString2JsonString
} from '../types/util'
import { simpleRunProjection } from '../utils/constants'
import { canMutate } from '../utils/roles'
import {
  currentProjectState,
  currentUserState,
  currentVehicleState,
  engineerListState,
  workflowTemplateListState
} from '../utils/state-atoms'

const defaultFilters: DataTableFilterMeta = {
  projectId: { value: undefined, matchMode: 'equals' }
}

const projection = encodeURIComponent(
  JSON.stringify({
    _id: 1,
    name: 1,
    shortName: 1,
    description: 1,
    overallStatus: 1,
    submissionRunGroups: {
      name: 1,
      workflowRunGroups: {
        name: 1,
        systemRunId: 1,
        overallStatus: 1,
        workflow: {
          name: 1,
          overallStatus: 1,
          timeUpdated: 1,
          _id: 1
        }
      }
    },
    runConfig: {
      description: 1,
      referenceRunName: 1,
      baselineRunName: 1
    }
  })
)

const EditProject: FC = () => {
  const params = useParams()
  const [breadcrumItems, setBreadcrumItems] = useState<BreadcrumbItem[]>([])
  const navigate = useNavigate()
  const engineers = useRecoilValue(engineerListState)
  const templates = useRecoilValue(workflowTemplateListState)
  const currentUser = useRecoilValue(currentUserState)
  const userCanMutate = canMutate(currentUser?.role?.roleId)
  const [showDetails, setShowDetails] = useState(false)
  const [showNewSubmission, setShowNewSubmission] = useState(false)
  const [isSavingSubmission, setIsSavingSubmission] = useState(false)
  const refDetails = useRef<Panel>(null)
  const [currentVehicle, setCurrentVehicle] = useRecoilState(currentVehicleState)
  const [submissions, setSubmissions] = useState<WorkflowSubmission[]>([])
  const [, setTotalRecords] = useState<number>(10)
  const [{ data: vehicleData }, fetchVehicle] = useGetVehicle()
  const [{ data: projData }, fetchProject] = useGetProject()
  const [currentProject, setCurrentProject] = useRecoilState(currentProjectState)
  const [{ loading: updatingProject }, updateProject] = useUpdateProject(params.id || '')
  const [, updateProjectDescription] = useUpdateProjectDescription()
  const [showProjectDescModal, setShowProjectDescModal] = useState(false)
  const [editingProjectDesc, setEditingProjectDesc] = useState('')
  const [, createSubmission] = useCreateRun()
  const [latestRuns, setLatestRuns] = useState<WorkflowSubmission[]>([])
  const [{ data: submissionData, loading: laodingRun }, fetchSubmissions] = useGetSubmissions()
  const [{ data: latestRunData }, fetchLatestRuns] = useGetSubmissions()

  const [lazyState, setLazyState] = useState<LazyTableState>({
    first: 0,
    rows: 0,
    page: 1,
    sortField: 'shortName',
    sortOrder: -1,
    filters: {
      ...defaultFilters
    },
    sumlvl: 'vehicleStatesList:0,geometryList:0,tyreSetList:0,cfdSequenceList:0,method:0'
  })

  useEffect(() => {
    let query
    if (lazyState.filters.projectId['value']) {
      const q = 'projectId=' + lazyState.filters.projectId['value']
      query = query ? `${query}&${q}` : q
    }

    if (!query) {
      return
    }

    query = queryString2JsonString(query)
    query = query ? `query=${query}&limit=${lazyState.rows}` : `limit=${lazyState.rows}`
    query = `${query}&proj=${projection}&skip=${lazyState.first}&countTotal=true`
    if (lazyState.sortField) {
      const sort = {
        [lazyState.sortField]: lazyState.sortOrder
      }
      query = `${query}&sort=${JSON.stringify(sort)}`
    }
    fetchSubmissions(query).catch(err => {
      console.error(err)
      toast.error('Error loading runs.')
    })
  }, [lazyState])

  const handleDataPage = (event: DataTablePageEvent) => {
    setLazyState({
      ...lazyState,
      first: 0,
      page: event.page as number,
      rows: 0
    })
  }

  const saveProject = async (projectFormData: Project) => {
    const newProj = new Project()
    newProj._id = currentProject?._id as string
    newProj.status = projectFormData.status
    newProj.isBaseline = projectFormData.isBaseline
    newProj.description = projectFormData.description
    newProj.outputParams = projectFormData.outputParams
    newProj.externalDocumentUrl = projectFormData.externalDocumentUrl
    newProj.area = projectFormData.area
    newProj.vehicleId = projectFormData.vehicleId
    newProj.engineer = cloneUser(projectFormData.engineer)
    if (projectFormData.methodSet) {
      newProj.methodSet = cloneMethodologySet(projectFormData.methodSet)
    }
    if (projectFormData.attitudeSet) {
      newProj.attitudeSet = cloneAttitudeSet(projectFormData.attitudeSet)
    }
    if (projectFormData.domainSet) {
      newProj.domainSet = cloneDomainSet(projectFormData.domainSet)
    }
    if (projectFormData.tyreSet) {
      newProj.tyreSet = cloneTyreSet(projectFormData.tyreSet)
    }
    if (projectFormData.simulationSet) {
      newProj.simulationSet = cloneSimulationSet(projectFormData.simulationSet)
    }
    newProj.referenceRun = projectFormData.referenceRun
    newProj.referenceRunName = projectFormData.referenceRunName
    newProj.baselineRunName = projectFormData.baselineRunName
    newProj.baselineRunId = projectFormData.baselineRunId
    newProj.baselineSubmissionTemplate = projectFormData.baselineSubmissionTemplate
    if (projectFormData.projectConfig) {
      newProj.projectConfig = new ProjectConfig(projectFormData.projectConfig)
    }
    if (projectFormData.baselineGeometry) {
      newProj.baselineGeometry = new Geometry(projectFormData.baselineGeometry)
    }

    if (projectFormData.contributorIds) {
      newProj.contributors = projectFormData.contributorIds.map(contributorId => {
        const contributor = engineers.find(eng => eng._id === contributorId)
        return cloneUser(contributor as User)
      })
    }

    try {
      const dataToPost = protoService.encodePayload(newProj, 'project')
      const res = await updateProject({
        data: dataToPost
      })

      if (res.data.protoBytesList && res.data.protoBytesList.length > 0) {
        const tempProj = protoService.deSerialize(res.data.protoBytesList[0], 'project') as Project
        tempProj.name = currentProject?.name as string
        tempProj.shortName = currentProject?.shortName as string

        // make sure this will be used to populate the template file in new run form
        if (tempProj.baselineSubmissionTemplate) {
          const selectedTemplate = templates.find(
            item => item._id === tempProj.baselineSubmissionTemplate
          )
          if (selectedTemplate) {
            tempProj.submissionTemplateObj = selectedTemplate as WorkflowSubmissionTemplate
          }
        }

        setCurrentProject(tempProj)
      }

      toast.success('Project updated.')
    } catch (err) {
      console.error(err)
      toast.error('Error saving project.')
    }
  }

  const openProjectDescModal = () => {
    setEditingProjectDesc(currentProject?.description || '')
    setShowProjectDescModal(true)
  }

  const handleProjectDescriptionChange = async () => {
    if (!currentProject?._id) {
      return
    }
    try {
      await updateProjectDescription({
        id: currentProject._id,
        description: editingProjectDesc
      } as any)
      setCurrentProject({ ...currentProject, description: editingProjectDesc } as Project)
      setShowProjectDescModal(false)
      toast.success('Project description updated.')
    } catch (err) {
      console.error(err)
      toast.error('Error updating project description.')
    }
  }

  const reopenProject = async () => {
    const newProj = new Project()
    newProj.status = ProjectStatus.ACTIVE
    newProj._id = currentProject?._id as string

    try {
      const dataToPost = protoService.encodePayload(newProj, 'project')
      await updateProject({
        data: dataToPost
      })

      toast.success('Project updated.')
      navigate(-1)
    } catch (err) {
      console.error(err)
      toast.error('Error saving project.')
    }
  }

  const handleCreateNewSubmission = () => {
    setShowNewSubmission(true)
  }

  useEffect(() => {
    params.id &&
      fetchProject(params.id).catch(err => {
        console.error(err)
        toast.error('Error loading project.')
      })

    fetchLatestRuns(`limit=10&proj=${simpleRunProjection}`)
  }, [params.id])

  useEffect(() => {
    if (latestRunData) {
      if (latestRunData.protoBytesList.length) {
        setLatestRuns(
          latestRunData.protoBytesList.map((item: string) => {
            const submission = protoService.deSerialize(item, 'workflowsubmission')
            return submission
          })
        )
      } else {
        setLatestRuns([
          {
            _id: '',
            name: ' '
            // name: 'No runs found'
          } as WorkflowSubmission
        ])
      }
    } else {
      setLatestRuns([
        {
          _id: '',
          name: ' '
          // name: 'No runs found'
        } as WorkflowSubmission
      ])
    }
  }, [latestRunData])

  useEffect(() => {
    if (projData && projData.protoBytesList.length) {
      const proj = protoService.deSerialize(projData.protoBytesList[0], 'project')

      if (templates.length) {
        const template = templates.find(item => item._id === proj.baselineSubmissionTemplate)
        if (template) {
          proj.submissionTemplateObj = template as WorkflowSubmissionTemplate
        }

        if (currentVehicle && currentVehicle._id === proj.vehicleId) {
          setBreadcrumItems([
            {
              title: 'CAR',
              name: currentVehicle.name,
              link: `/projects?vehicleId=${currentVehicle._id}`
            },
            {
              title: 'PROJECT',
              name: proj?.shortName || '',
              description: proj?.description || ''
            }
          ])
        } else {
          fetchVehicle(proj.vehicleId)
        }
      }

      setCurrentProject(proj)
      setLazyState({
        ...lazyState,
        filters: {
          projectId: {
            value: proj._id,
            matchMode: 'equals'
          }
        }
      })
    }
  }, [projData])

  useEffect(() => {
    if (vehicleData && vehicleData.protoBytesList.length) {
      const vehicle = protoService.deSerialize(vehicleData.protoBytesList[0], 'vehicle')
      setCurrentVehicle(vehicle)
      setBreadcrumItems([
        {
          title: 'CAR',
          name: `${vehicle.vehicleId}`,
          link: `/projects?vehicleId=${vehicle._id}`
        },
        {
          title: 'PROJECT',
          name: currentProject?.shortName || '',
          description: currentProject?.description || ''
        }
      ])
    }
  }, [vehicleData])

  useEffect(() => {
    if (submissionData) {
      const runList = submissionData.protoBytesList.map((item: string) => {
        const submission = protoService.deSerialize(item, 'workflowsubmission')
        return submission
      })
      setSubmissions(runList)

      if (submissionData.resultString) {
        const result = JSON.parse(submissionData.resultString)
        setTotalRecords(result.totalCount)
      }
    }
  }, [submissionData])

  const saveSubmission = async (
    runConfig: RunConfigProto,
    submission: WorkflowSubmission,
    aeroTempRunId?: string | null,
    gmid?: string,
    simUploadPromise?: Promise<any>,
    setupSimProgress?: (cb: (pct: number) => void) => void,
    geoUploadPromise?: Promise<any>,
    setupGeoProgress?: (cb: (pct: number) => void) => void,
    sequenceNumberOverride?: number | null,
    simOverrideSourcePath?: string | null
  ) => {
    //save submission
    if (isSavingSubmission) return
    setIsSavingSubmission(true)
    submission.overallStatus = RunStatus.CREATED
    let newWorkflowSubmission: WorkflowSubmission
    try {
      // prepare workflow submission
      submission.runConfig = runConfig
      const dataToPost = protoService.encodePayload(submission, 'workflowsubmission')

      // Create request body with data_base64 and optional temp run ID and gmid
      const requestBody: any = {
        data_base64: dataToPost.data_base64
      }
      if (aeroTempRunId) {
        requestBody.aeroTempRunId = aeroTempRunId
        if (gmid) {
          requestBody.gmid = gmid
        }
      }
      // Dropdown-selected override template (no upload): tell the server where to copy the .sim
      // from so it lands in this run's own staging_overrides folder. gmid is required for the dest.
      if (simOverrideSourcePath && gmid) {
        requestBody.simOverrideSourcePath = simOverrideSourcePath
        requestBody.gmid = gmid
      }
      // If sim file is still uploading, skip the server-side copy now;
      // the store will trigger it via finalize-sim-upload once the browser upload finishes.
      if (simUploadPromise) {
        requestBody.skipSimFileCopy = true
      }
      // If geo files are still uploading, skip the server-side copy now;
      // the store will trigger it via finalize-geo-upload once the browser upload finishes.
      if (geoUploadPromise) {
        requestBody.skipGeoFileCopy = true
      }
      // Pass sequence number override if user specified one
      if (sequenceNumberOverride != null) {
        requestBody.sequenceNumberOverride = sequenceNumberOverride
      }
      console.log('Request body to submit:', requestBody)

      const submissionRes = await createSubmission({ data: requestBody })
      newWorkflowSubmission = protoService.deSerialize(
        submissionRes.data.protoBytesList[0],
        'workflowsubmission'
      )

      // Clear temporary run ID from localStorage after successful save
      localStorage.removeItem('aero_temp_run_id')

      // Register background upload if sim file was still in transit
      if (simUploadPromise && aeroTempRunId && gmid) {
        simUploadQueueStore.register(
          {
            runId: newWorkflowSubmission._id,
            runName: newWorkflowSubmission.name,
            fileName: newWorkflowSubmission.runConfig?.overrideSelectedSimFile || 'sim file',
            aeroTempRunId,
            gmid
          },
          simUploadPromise
        )
        // Wire up live progress → store now that we know the runId
        setupSimProgress?.(pct =>
          simUploadQueueStore.updateProgress(newWorkflowSubmission._id, pct)
        )
        toast.success(
          'Run saved! Sim file is uploading in the background – watch the progress bar.'
        )
      }

      // Register background upload if geo files were still in transit
      if (geoUploadPromise && aeroTempRunId && gmid) {
        const geoTrackingId = `${newWorkflowSubmission._id}-geo`
        const geoFileNames =
          newWorkflowSubmission.runConfig?.geoStagingFiles?.join(', ') ||
          newWorkflowSubmission.runConfig?.geometryChangeImage?.join(', ') ||
          'geometry files'
        simUploadQueueStore.register(
          {
            runId: geoTrackingId,
            apiRunId: newWorkflowSubmission._id,
            runName: newWorkflowSubmission.name,
            fileName: geoFileNames,
            aeroTempRunId,
            gmid,
            finalizeEndpoint: 'finalize-geo-upload'
          },
          geoUploadPromise
        )
        setupGeoProgress?.(pct => simUploadQueueStore.updateProgress(geoTrackingId, pct))
        toast.success(
          'Run saved! Geometry files are uploading in the background – watch the progress bar.'
        )
      }

      if (!simUploadPromise && !geoUploadPromise) {
        toast.success('Workflow submission saved.')
      }

      setShowNewSubmission(false)
      navigate('/submissions/' + newWorkflowSubmission.name)
    } catch (err: any) {
      console.error(err)
      const serverMessage = err?.response?.data
      if (err?.response?.status === 409 && serverMessage) {
        toast.error(serverMessage)
      } else {
        toast.error('Error saving workflow submission.')
      }
      return
    } finally {
      setIsSavingSubmission(false)
    }
  }

  return (
    <>
      <div className="relative w-full flex flex-col items-stretch pb-10">
        <div className="flex items-center justify-between py-6">
          <Breadcrumb items={breadcrumItems} />
          <div className="flex items-center justify-between">
            {userCanMutate && (
              <Button
                onClick={handleCreateNewSubmission}
                severity="secondary"
                className="icon-button"
                tooltip="Create New Run"
                tooltipOptions={{ position: 'bottom' }}
                data-pr-at={!showDetails ? 'center+50 bottom' : 'center+37 bottom'}>
                <PlusIcon className="fill-white" />
              </Button>
            )}
          </div>
        </div>
        <Panel
          ref={refDetails}
          toggleable
          collapsed={!showDetails}
          header={
            <div className="flex items-center justify-between space-x-1 ">
              <Button
                className="icon-button bg-transparent "
                link
                onClick={evt => {
                  refDetails.current?.toggle(evt)
                }}>
                {!showDetails && (
                  <ExpandIcon className="fill-dark-blue dark:fill-neutral-10 h-6 w-6" />
                )}
                {showDetails && (
                  <CollapseIcon className="fill-dark-blue dark:fill-neutral-10 h-6 w-6" />
                )}
              </Button>
              <span className="text-neutral-90 dark:text-light-blue text-lg">Project Details</span>
              <div className="flex items-center justify-end grow pr-2">
                <IconButton
                  clear
                  title="Edit Description"
                  icon={<EditIcon className="fill-gm-blue dark:fill-light-blue w-4 h-4" />}
                  onClick={evt => {
                    evt.stopPropagation()
                    openProjectDescModal()
                  }}
                />
              </div>
            </div>
          }
          onExpand={e => setShowDetails(true)}
          onCollapse={e => setShowDetails(false)}
          onAnimationEnd={e => {
            console.log(e)
          }}
          pt={{
            root: {
              className: 'rounded-lg shadow-md dark:border-neutral-90'
            },
            toggler: {
              className: 'hidden'
            },
            header: {
              className: classNames('bg-alt-gray dark:bg-neutral-80 dark:border-neutral-90 !p-2', {
                'rounded-b-none': showDetails,
                'rounded-b-md': !showDetails
              })
            },
            content: { className: 'p-4 dark:bg-neutral-80 dark:border-neutral-90' }
          }}>
          {currentProject && showDetails && (
            <ProjectForm
              saving={updatingProject}
              onSave={saveProject}
              onReopen={reopenProject}
              initProject={currentProject as Project}
              mode={
                currentProject && currentProject.status === ProjectStatus.ARCHIVED ? 'view' : 'edit'
              }
            />
          )}
        </Panel>
        <div className="text-gm-primary-90 text-xl mt-8 dark:text-light-blue">Run List</div>
        <div>
          <SubmissionList
            submissions={submissions}
            loading={laodingRun}
            onPageChange={handleDataPage}
          />
        </div>
      </div>
      {showNewSubmission && (
        <SubmissionModal
          mode="new"
          submitting={isSavingSubmission}
          latestRuns={latestRuns}
          project={currentProject as Project}
          onCancel={() => setShowNewSubmission(false)}
          onConfirm={(
            runconfig,
            submission,
            aeroTempRunId,
            gmid,
            simUploadPromise,
            setupSimProgress,
            geoUploadPromise,
            setupGeoProgress,
            sequenceNumberOverride,
            simOverrideSourcePath
          ) => {
            console.log(runconfig, submission)
            saveSubmission(
              runconfig,
              submission,
              aeroTempRunId,
              gmid,
              simUploadPromise,
              setupSimProgress,
              geoUploadPromise,
              setupGeoProgress,
              sequenceNumberOverride,
              simOverrideSourcePath
            )
          }}
        />
      )}

      <Dialog
        visible={showProjectDescModal}
        onHide={() => setShowProjectDescModal(false)}
        header="Edit Description"
        modal
        style={{ width: '50vw' }}
        breakpoints={{ '960px': '75vw', '640px': '90vw' }}>
        <div className="flex flex-col space-y-4">
          <div>
            <label htmlFor="project-description" className="block text-sm font-medium mb-2">
              Description
            </label>
            <InputTextarea
              id="project-description"
              value={editingProjectDesc}
              onChange={e => setEditingProjectDesc(e.target.value)}
              rows={5}
              className="w-full"
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              label="Cancel"
              severity="secondary"
              onClick={() => setShowProjectDescModal(false)}
              pt={{
                root: {
                  className: 'bg-gray-600 dark:!bg-white'
                },
                label: {
                  className: 'text-white dark:!text-black'
                }
              }}
            />
            <Button
              label="Save"
              onClick={handleProjectDescriptionChange}
              disabled={!userCanMutate}
            />
          </div>
        </div>
      </Dialog>
    </>
  )
}

export default EditProject
